import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { Input } from "components/Graphs/Input";
import { Button } from "components/Graphs/Button";
import CopyButton from "components/Graphs/CopyButton";

interface RemoveNodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeName: string;
  onConfirm: () => Promise<void>;
}

const RemoveNodeDialog: React.FC<RemoveNodeDialogProps> = ({
  open,
  onOpenChange,
  nodeName,
  onConfirm,
}) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [isRemoving, setIsRemoving] = useState(false);

  const expectedConfirmation = nodeName.trim();
  const isConfirmationValid = confirmationText.trim() === expectedConfirmation;

  const handleClose = () => {
    setConfirmationText("");
    setIsRemoving(false);
    onOpenChange(false);
  };

  const handleRemove = async () => {
    if (!isConfirmationValid) return;
    setIsRemoving(true);
    try {
      await onConfirm();
      handleClose();
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="pb-2 text-white">Remove Node</DialogTitle>
          <DialogDescription asChild className="text-gray-300">
            <div>
              <p>
                This will remove the node&apos;s access from Brev. All exposed
                ports will become unreachable.
              </p>
              <p className="mt-2">
                To fully clean up the device (remove Brev SSH keys and uninstall
                the Brev tunnel), run{" "}
                <code className="rounded bg-gray-800 px-1 py-0.5 font-mono text-white">
                  brev deregister
                </code>{" "}
                from the device.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm text-secondary">
              <label
                id="node-confirmation-label"
                htmlFor="node-confirmation-input"
              >
                Type{" "}
                <span className="font-bold text-white">
                  {expectedConfirmation}
                </span>
              </label>
              <CopyButton
                value={expectedConfirmation}
                displayValue={<></>}
                className="!inline-flex !gap-0 align-middle mx-1"
              />
              <span id="node-confirmation-instructions">
                to confirm removal.
              </span>
            </div>
            <Input
              id="node-confirmation-input"
              aria-labelledby="node-confirmation-label node-confirmation-instructions"
              type="text"
              placeholder={expectedConfirmation}
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isConfirmationValid) handleRemove();
              }}
              disabled={isRemoving}
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isRemoving}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemove}
            disabled={!isConfirmationValid || isRemoving}
            isLoading={isRemoving}
          >
            {isRemoving ? "Removing..." : "Remove Node"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { RemoveNodeDialog };
